// src/auth/AuthContext.jsx
import React, {
  createContext,
  useContext,
  useRef,
  useState
} from "react";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext(null);

class AuthError extends Error {
  constructor(message, status = 401) {
    super(message);
    this.name = "AuthError";
    this.status = status;
  }
}

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(() =>
    sessionStorage.getItem("access_token")
  );
  const [idToken, setIdToken] = useState(() =>
    sessionStorage.getItem("id_token")
  );

  const refreshTokenRef = useRef(
    sessionStorage.getItem("refresh_token")
  );

  const refreshInFlightRef = useRef(null);

  const persistTokens = ({ accessToken, idToken }) => {
    if (typeof accessToken === "string") {
      sessionStorage.setItem("access_token", accessToken);
    }
    if (typeof idToken === "string") {
      sessionStorage.setItem("id_token", idToken);
    }
  };

  const clearLocalSession = () => {
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("id_token");
    sessionStorage.removeItem("refresh_token");
    refreshTokenRef.current = null;
    setAccessToken(null);
    setIdToken(null);
  };

  const getTokenExpiryMs = (token) => {
    if (!token) return null;
    try {
      const { exp } = jwtDecode(token);
      if (!exp) return null;
      return exp * 1000;
    } catch {
      return null;
    }
  };

  const isTokenValid = (token) => {
    const expMs = getTokenExpiryMs(token);
    if (!expMs) return false;
    return expMs > Date.now();
  };

  const isTokenExpiringSoon = (token, windowMs) => {
    const expMs = getTokenExpiryMs(token);
    if (!expMs) return true;
    return expMs - Date.now() < windowMs;
  };

  /* ------------------------- Pure checks ------------------------- */

  const isAuthenticated = () => {
    return isTokenValid(idToken);
  };

  const needsRefresh = () => {
    const refreshWindowMs = 5 * 60 * 1000; // 5 minutes

    // If tokens are missing (e.g. after page refresh) we should refresh
    // rather than treating the session as logged out.
    if (!accessToken || !idToken) return true;

    // Refresh if either token is expired or close to expiry.
    return (
      isTokenExpiringSoon(accessToken, refreshWindowMs) ||
      isTokenExpiringSoon(idToken, refreshWindowMs)
    );
  };

  const getAccessToken = () => accessToken;

  /* ------------------------- OAuth flows ------------------------- */

  const login = () => {
    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_APP_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_COGNITO_CALLBACK_URL;

    const url =
      `https://${domain}/oauth2/authorize` +
      `?response_type=code` +
      `&client_id=${clientId}` +
      `&redirect_uri=${redirectUri}`;

    window.location.href = url;
  };

  const logout = () => {
    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_APP_CLIENT_ID;
    const logoutUri = process.env.REACT_APP_COGNITO_SIGNOUT_URL;

    clearLocalSession();

    window.location.href =
      `https://${domain}/logout` +
      `?client_id=${clientId}` +
      `&logout_uri=${logoutUri}`;
  };

  /* -------------------- Token exchange & refresh -------------------- */

  const exchangeCodeForTokens = async (code) => {
    const domain = process.env.REACT_APP_COGNITO_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_APP_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_COGNITO_CALLBACK_URL;

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      code,
      client_id: clientId,
      redirect_uri: redirectUri,
    });

    const res = await fetch(`https://${domain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });

    const payload = await res.json();

    if (!res.ok) {
      throw new AuthError(
        payload?.error_description || "Token exchange failed",
        res.status
      );
    }

    setAccessToken(payload.access_token);
    setIdToken(payload.id_token);
    persistTokens({
      accessToken: payload.access_token,
      idToken: payload.id_token,
    });

    refreshTokenRef.current = payload.refresh_token;
    sessionStorage.setItem("refresh_token", payload.refresh_token);
  };

  const ensureValidToken = async () => {
    if (!needsRefresh()) return;

    if (!refreshTokenRef.current) {
      throw new AuthError("No refresh token", 401);
    }

    if (!refreshInFlightRef.current) {
      refreshInFlightRef.current = (async () => {
        const domain = process.env.REACT_APP_COGNITO_DOMAIN;
        const clientId = process.env.REACT_APP_COGNITO_APP_CLIENT_ID;

        const body = new URLSearchParams({
          grant_type: "refresh_token",
          client_id: clientId,
          refresh_token: refreshTokenRef.current,
        });

        const res = await fetch(`https://${domain}/oauth2/token`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });

        const payload = await res.json();

        if (!res.ok) {
          // Clear local tokens so route guards/API calls can redirect to login.
          // Avoid forcing a Cognito logout redirect here.
          clearLocalSession();
          throw new AuthError(payload?.error_description || "Session expired", 401);
        }

        if (payload.access_token) {
          setAccessToken(payload.access_token);
        }
        if (payload.id_token) {
          setIdToken(payload.id_token);
        }

        persistTokens({
          accessToken: payload.access_token,
          idToken: payload.id_token,
        });
      })().finally(() => {
        refreshInFlightRef.current = null;
      });
    }

    return refreshInFlightRef.current;
  };

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        exchangeCodeForTokens,
        ensureValidToken,
        isAuthenticated,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
